import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useState } from 'react';
import {
  $getSelection,
  $isRangeSelection,
  $isRootNode,
  $isTextNode,
} from 'lexical';
import { createPortal } from 'react-dom';
import { Button } from 'antd';
import { CommentOutlined } from '@ant-design/icons';
import InlineCommentBox from './InlineCommentBox';
import httpInjectorService from 'services/http-injector.service';
import { toast } from 'react-toastify';
import { getColorForUsername } from 'components/utils/AvatarColor';
import { setSpaceAssigneeList } from 'store/actions';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { restoreHighlights, highlightSelection } from './CustomNode';
import CommentClickPlugin from './CommentClickPlugin';

export default function FloatingCommentPlugin({ spaceid, pageid,HandleEditContent }) {
  const [editor] = useLexicalComposerContext();

  const [rect, setRect] = useState(null);
  const [visible, setVisible] = useState(false);
  const [selectionRange, setSelectionRange] = useState(null);

  const [comments, setComments] = useState([]);
  const [activeComment, setActiveComment] = useState(null);
  const [showCommentBox, setShowCommentBox] = useState(false);

  const cookieUsername = Cookies.get('username') || 'User';

  const currentUser = {
    name: cookieUsername,
  };

  const dispatch = useDispatch();

  const assigneeOptions = useSelector(
    (state) => state.Authentication?.spaceassignees,
  );

  function getBlockOffset(node, offset) {
    let total = offset;
    let current = node;

    while (current && !$isRootNode(current.getParent())) {
      let prev = current.getPreviousSibling();
      while (prev) {
        if ($isTextNode(prev) || prev.getTextContent) {
          total += prev.getTextContent().length;
        }
        prev = prev.getPreviousSibling();
      }
      current = current.getParent();
    }
    return total;
  }

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection) && !selection.isCollapsed()) {
          // Debounce to avoid rapid updates
          const domRect = window
            .getSelection()
            ?.getRangeAt(0)
            ?.getBoundingClientRect();
          if (domRect) {
            setRect(domRect);
            setVisible(true);

            const anchorNode = selection.anchor.getNode();
            const focusNode = selection.focus.getNode();
            const blockNode = anchorNode.getTopLevelElementOrThrow();

            const start = getBlockOffset(anchorNode, selection.anchor.offset);
            const end = getBlockOffset(focusNode, selection.focus.offset);

            setSelectionRange({
              block_id: blockNode.getKey(),
              start_offset: Math.min(start, end),
              end_offset: Math.max(start, end),
              text: selection.getTextContent(),
            });
          }
        } else {
          // Delay hide to avoid flicker
          setTimeout(() => setVisible(false), 100);
        }
      });
    });
  }, [editor]);

  const hexToRgb = (hex) => {
    if (!hex) return '';
    hex = hex.replace('#', '');

    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `rgb(${r}, ${g}, ${b})`;
  };

  const extractMentionIds = (text) => {
    if (!text) return [];

    const regex = /@(\w+)/g;
    const matches = [...text.matchAll(regex)];

    const ids = matches
      .map((m) => {
        const username = m[1];

        const user = assigneeOptions?.find(
          (u) => u.username.toLowerCase() === username.toLowerCase(),
        );

        return user?.user_id;
      })
      .filter(Boolean);

    return ids;
  };

  const mentionUsers =
    assigneeOptions?.map((u) => ({
      id: u.user_id,
      value: u.username,
    })) || [];

  const getSpaceUsers = async () => {
    try {
      const res = await httpInjectorService.getSpaceUser(spaceid);

      if (res.status === 'success') {
        dispatch(setSpaceAssigneeList(res.data));
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load users');
    }
  };

  useEffect(() => {
    getSpaceUsers();
    getPageComments();
  }, []);

  const getInitials = (username) =>
    username
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase();

  const handleCommentClick = () => {
    setShowCommentBox(true);
    setVisible(false);
    setActiveComment(null);
  };

  const handleReply = async (text, parentId = null) => {
    if (!text) return;
    const username = Cookies.get('username');

    try {
      const assignerHex = getColorForUsername(username);
      const assignerRgb = hexToRgb(assignerHex);

      const mentionedUsers = extractMentionIds(text);

      let parent_comment_id = null;

      if (parentId) {
        parent_comment_id = parentId;
      } else if (activeComment?.id) {
        parent_comment_id = activeComment.id;
      }
      let inline_position = null;
      if (parent_comment_id) {
        const parentComment = findComment(comments, parent_comment_id);
        inline_position =
          parentComment?.inline_position || selectionRange || null;
      } else {
        inline_position = selectionRange || null;
      }

      const payload = {
        space_id: spaceid,
        item_id: pageid,
        parent_comment_id: parent_comment_id,
        type: 'inline',
        inline_position: inline_position,
        content: text,
        mentions: mentionedUsers,
        assigner_avatar: [
          {
            assigner_avatar_code: getInitials(username),
            assigner_avatar_color_code: assignerRgb,
          },
        ],
      };

      const response = await httpInjectorService.postpageComment(payload);

      if (response?.status === 'success') {
        await getPageComments();
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to reply');
    }
  };

  const handleEdit = async (id, text) => {
    if (!activeComment) return;

    try {
      const payload = {
        id: id,
        space_id: spaceid,
        item_id: pageid,
        content: text,
      };

      const response = await httpInjectorService.editPageComment(payload);

      if (!id) {
        console.error('ID is missing!');
        return;
      }

      if (response?.status === 'success') {
        await getPageComments();
        toast.success('Comment updated');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to update comment');
    }
  };

  const findComment = (list, id) => {
    for (let c of list) {
      if (String(c.id) === String(id)) return c;

      if (c.replies?.length) {
        const found = findComment(c.replies, id);
        if (found) return found;
      }
    }
    return null;
  };

  const handleAddComment = async (text) => {
    const username = Cookies.get('username');

    if (!text) return;

    try {
      const assignerHex = getColorForUsername(username);
      const assignerRgb = hexToRgb(assignerHex);

      const mentionedUsers = extractMentionIds(text);

      const payload = {
        space_id: spaceid,
        item_id: pageid,
        parent_comment_id: null,
        type: 'inline',
        inline_position: {
          block_id: selectionRange?.block_id || null,
          start_offset: selectionRange?.start_offset || 0,
          end_offset: selectionRange?.end_offset || 0,
          text: selectionRange?.text || '',
        },
        content: text,
        mentions: mentionedUsers,
        assigner_avatar: [
          {
            assigner_avatar_code: getInitials(username),
            assigner_avatar_color_code: assignerRgb,
          },
        ],
      };

      const response = await httpInjectorService.postpageComment(payload);

      if (response?.status === 'success') {
        // await HandleEditContent()
        const newCommentId = response?.data?.id || response?.data?.comment_id;

        const updatedComments = await getPageComments();

        const newComment = findComment(updatedComments || [], newCommentId);

        if (newComment) {
          setActiveComment(newComment);
        } else {
          const now = new Date();
          setActiveComment({
            id: newCommentId,
            text,
            inline_position: selectionRange,
            replies: [],
            user: { name: Cookies.get('username') || cookieUsername },
            created_at: now.toISOString(),
            date: now.toLocaleDateString(),
            time: now.toLocaleTimeString(),
          });
        }

        setShowCommentBox(true);

        // setTimeout(() => {
        //   highlightSelection(editor, newCommentId);

        //   window.getSelection()?.removeAllRanges();
        // }, 50);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to add comment');
    }
  };

const formatComments = (list) => {
  return list.map((c) => {
    const d = new Date(c.created_at); 

    return {
      id: c.id,
      text: c.content,
      inline_position: c.inline_position,
      parent_comment_id: c.parent_comment_id || null,
      user: { name: c.creator_name || 'User', avatar: '' },
      created_at: c.created_at,
      date: d.toLocaleDateString(),
      time: d.toLocaleTimeString(),
      replies: c.replies ? formatComments(c.replies) : [],
    };
  });
};

  const getPageComments = async () => {
    try {
      const response = await httpInjectorService.getPageComment(
        spaceid,
        pageid,
        'inline',
      );

      if (response?.status === 'success') {
        const formatted = formatComments(response.data || []);

        setComments(formatted);

        if (activeComment?.id) {
          const updated = findComment(formatted, activeComment.id);
          if (updated) {
            setActiveComment(updated);
          } else {
            setActiveComment(null);
            setShowCommentBox(false);
          }
        }

        restoreHighlights(editor, formatted);

        return formatted;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await httpInjectorService.deletePageComment(
        spaceid,
        pageid,
        id,
      );

      if (response?.status === 'success') {
        await getPageComments();
        toast.success('Comment deleted');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete comment');
    }
  };

  return (
    <>
      <CommentClickPlugin
        onCommentClick={(id) => {
          const comment = findComment(comments, id);
          if (comment) {
            setActiveComment(comment);
            setShowCommentBox(true);
          }
        }}
      />
      {visible &&
        rect &&
        createPortal(
          <div
            style={{
              position: 'absolute',
              top: rect.top + window.scrollY - 60,
              left: rect.left + window.scrollX,
              zIndex: 1000,
            }}
          >
            <Button size="large" onClick={handleCommentClick}>
              <CommentOutlined /> Comment
            </Button>
          </div>,
          document.body,
        )}

      <InlineCommentBox
        visible={showCommentBox || !!activeComment}
        comment={activeComment}
        currentUser={currentUser}
        onAdd={handleAddComment}
        onReply={handleReply}
        onDelete={handleDelete}
        onEdit={handleEdit}
        onClose={() => {
          setActiveComment(null);
          setShowCommentBox(false);
        }}
      />
    </>
  );
}
